import axios from 'axios';

import type { PurchaseOrderUpdate } from '@apple/features/order/models/approval';
import type { Comment, ItemQuantity, Message } from '@apple/features/order/models/details';
import { commentSchema } from '@apple/features/order/models/details';
import type { ProductKey } from '@apple/features/product/models/product';

import type { OrderDetailResponse, OrderLineItem } from '../models/details';
import { orderDetailResponseSchema, orderLineItemSchema } from '../models/details';

export async function getOrderDetail(
	poNumber: string,
	signal?: AbortSignal,
): Promise<OrderDetailResponse> {
	const data = (await axios.get<unknown>(`/api/orders/${poNumber}`, { signal })).data;

	return orderDetailResponseSchema.parse(data);
}

export async function getPurchaseOrderLines(
	poNumber: string,
	signal?: AbortSignal,
): Promise<OrderLineItem[]> {
	const data = (await axios.get<unknown>(`/api/orders/${poNumber}/line-items`, { signal })).data;
	return orderLineItemSchema.array().parse(data);
}

export async function updateOrder(orderUpdateModel: PurchaseOrderUpdate): Promise<void> {
	await axios.post('api/approval-queue/save', orderUpdateModel);
}

export async function getComments(poNumber: string, signal?: AbortSignal) {
	const data = (await axios.get<unknown>(`/api/orders/${poNumber}/comments`, { signal })).data;
	return commentSchema.array().parse(data);
}

export async function saveComment(message: Message, poNumber: string): Promise<Comment> {
	const data = (await axios.post<unknown>(`/api/orders/${poNumber}/comments/add`, message)).data;
	return commentSchema.parse(data);
}

export async function requestLineItemCancellation(
	poNumber: string,
	itemId: ProductKey,
): Promise<void> {
	await axios.post(`/api/orders/${poNumber}/${itemId}/request-cancellation`);
}

export async function requestOrderCancellation(purchaseOrderNumber: string) {
	await axios.post(`/api/order-cancellation/${purchaseOrderNumber}/cancel`);
}

export async function addLine(lineItem: ItemQuantity, poNumber: string): Promise<void> {
	await axios.post<unknown>(`/api/orders/${poNumber}/add-line`, lineItem);
}

export async function changeLineItemBypassBudget(
	poNumber: string,
	itemId: ProductKey,
	bypassBudget: boolean,
): Promise<void> {
	await axios.post(`/api/orders/${poNumber}/${itemId}/bypass-budget`, { bypassBudget });
}
